// =============================================================================
// Ledger intent - the descriptive, namespaced classifier for WHY a
// `LedgerEntrySet` was posted (e.g. `'invoice.issue'`, `'payment.settle'`).
//
// Naming convention, enforced by `tests/ledger-intent.test.ts`:
//
//     <owning-domain>[.<counterparty-domain>].<verb>[.reversal]
//
//   - `<owning-domain>` MUST be a directory name under
//     `apps/backend/platform/src/domains/`. This is what makes the convention
//     mechanically checkable rather than a style preference.
//   - `<counterparty-domain>` is OPTIONAL and, when present, MUST also be a real
//     domain directory. It names the domain whose document the movement acted
//     ON, where the owning domain alone would flatten a real distinction - a
//     payment settling an invoice, a supplier invoice, or a credit note. Absent
//     when there is no counterparty (`payment.settle` settles no document), and
//     its absence carries that meaning.
//   - `<verb>` is present-tense imperative and names the REASON for the money
//     movement (`issue`, `settle`, `allocate`), never a noun-ised event
//     (`recognition`, `settlement`, `allocation`).
//   - `.reversal` is the single sanctioned suffix. It is derived by
//     `reverseLedgerEntrySet` from the original row; a reversal is never itself
//     reversed, so `.reversal.reversal` cannot arise.
//   - Everything is kebab-case:
//     /^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*){1,2}(\.reversal)?$/
//
// Deliberately NOT a Prisma enum: a new intent then needs no migration, and the
// DB column stays a plain string. Deliberately absent from
// `CreateLedgerEntrySetInputSchema` (the HTTP body schema) - intent is a
// trusted, server-derived classifier, never client input. The manual-journal
// route stamps `ledger.post-manual-journal` server-side rather than spreading the body.
// =============================================================================
import { z } from 'zod';
/**
 * The canonical catalogue. The declaration IS the enforcement: adding an intent
 * here is the only way to make it postable, and the `source` it declares is
 * checked at the choke point on every write.
 *
 * The source *domain* is not stored on the row - it is this table's `source`,
 * looked up from the intent's first component. Storing both would let the pair
 * drift.
 */
export const LEDGER_INTENTS = {
    // --- invoice ------------------------------------------------------------
    /** Revenue recognition when an invoice is issued. */
    'invoice.issue': { source: 'invoice' },
    /** Re-tag of an opening-balance invoice onto its own AR open item. */
    'invoice.retag-opening': { source: 'invoice' },
    // --- supplier-invoice ---------------------------------------------------
    /** Payable raised when an expense (supplier invoice) is approved. */
    'supplier-invoice.approve': { source: 'supplier-invoice' },
    /** Net-zero itemisation of an imported opening payable. */
    'supplier-invoice.retag-opening': { source: 'supplier-invoice' },
    /** Supplier credit note or discount recorded against a payable. */
    'supplier-invoice.credit': { source: 'supplier-invoice' },
    // --- credit-note --------------------------------------------------------
    /** Credit note issued against a client invoice. */
    'credit-note.issue': { source: 'credit-note' },
    // NOTE: there is no `credit-note.apply` / `credit-note.refund`, and no
    // `invoice.write-off`. Applying credit, refunding it, and writing off a bad
    // debt all mint a Payment via `recordPayment` and post through
    // `postSettlementJournal`, so the journal they produce belongs to the payment
    // family below - a credit-note refund posts `payment.credit-note.settle`, and
    // a write-off posts `payment.invoice.settle`. Declaring them here instead
    // would mean declaring a source domain the posting cannot supply: the journal
    // is keyed `payment-<id>` and linked to `Payment.ledgerEntrySetId`, not to the
    // invoice or credit note.
    //
    // So the counterparty IS named - just on the payment side, where the posting
    // actually happens. What still lives only on the Payment record is the finer
    // distinction between an ordinary settlement and a write-off, since both
    // settle an invoice. Separating those would need a real discriminator on
    // Payment first; deriving one by inspecting the funding account's role would
    // be exactly the kind of derivation the data model should record as a fact.
    // --- payment ------------------------------------------------------------
    // The settlement family carries a THIRD component naming the counterparty
    // domain whose document was settled. It is derived at post time from the
    // payment's own validated shape (`deriveSettlementIntent`), never declared by
    // a caller, so it cannot disagree with what the journal actually did.
    //
    // The type is homogeneous per payment by construction: `controlRole` resolves
    // once per payment from kind + party, AP rejects a non-supplier-invoice
    // allocation, an IN payment rejects a non-invoice allocation, and a TRUST
    // payment is forced document-free. So there is always exactly one answer.
    //
    // `sourceEntityId` is the PAYMENT id for all four - the counterparty document
    // is already carried per-leg by the open-item tags. This component classifies
    // the journal; it does not point at anything.
    /** Client payment settling one or more invoices. */
    'payment.invoice.settle': { source: 'payment' },
    /** Money-out payment settling one or more supplier invoices. */
    'payment.supplier-invoice.settle': { source: 'payment' },
    /** Money-out payment refunding a client credit note. */
    'payment.credit-note.settle': { source: 'payment' },
    /**
     * Document-free settlement: a payment with no allocations, so nothing was
     * settled and there is no counterparty domain to name. The absent middle
     * component IS the meaning. Covers an on-account client overpayment, a
     * document-free refund, and every TRUST payment (which `recordPayment` forces
     * to zero allocations).
     */
    'payment.settle': { source: 'payment' },
    // --- bank-statement-line ------------------------------------------------
    /**
     * Dr Bank / Cr Bank Suspense, posted when a statement line is imported.
     *
     * Sources its line like every other document-backed intent. That is possible
     * because the import path creates the LINE first and posts this journal
     * second - matching how `approveInvoice` and `approveSupplierInvoice` post
     * against a document that already exists. `BankStatementLine.importEntrySetId`
     * still carries the link in the other direction; the two are set in adjacent
     * writes.
     */
    'bank-statement-line.import': { source: 'bank-statement-line' },
    /** Suspense cleared to its real account when the line is allocated. */
    'bank-statement-line.allocate': { source: 'bank-statement-line' },
    /** Legacy fused journal reshaped onto the current clearing shape. */
    'bank-statement-line.converge': { source: 'bank-statement-line' },
    // Trust interest / LPFF liability cycle. Each classifies one observed line
    // kind on a trust bank's Rule 54.14.16 interest cycle, posting to the
    // existing TRUST_INTEREST_PAYABLE_LPFF or TRUST_CREDITORS accounts, or to
    // TRUST_RECOVERABLE_FROM_BUSINESS for firm-borne charges. The intent carries
    // what the movement was.
    /** Interest credit recognised on a trust bank line (both legs: Fund share, and the client's share on a s 86(4) account). */
    'bank-statement-line.record-trust-interest': { source: 'bank-statement-line' },
    /** The observed AMTS sweep debiting a trust bank's interest payable to the Fidelity Fund. */
    'bank-statement-line.record-lpff-transfer': { source: 'bank-statement-line' },
    /** A trust bank charge attributed as Fund-claimable (s 57(2)(a); s 86(2) and s 86(3) accounts only). */
    'bank-statement-line.record-trust-charge-fund': { source: 'bank-statement-line' },
    /** A trust bank charge attributed as recoverable from a named client (TRUST_CREDITORS leg). */
    'bank-statement-line.record-trust-charge-client': { source: 'bank-statement-line' },
    /** A trust bank charge attributed as borne by the firm. */
    'bank-statement-line.record-trust-charge-firm': { source: 'bank-statement-line' },
    /** A trust bank charge split between the Fund and the firm in a single journal. */
    'bank-statement-line.record-trust-charge-split': { source: 'bank-statement-line' },
    /**
     * The monthly bank-initiated VAT redirect crediting a Fund-claimable
     * charge's VAT portion back to trust (observed on every trust bank
     * account every month - the bank charges VAT-inclusive, then redirects
     * the VAT to the business account). Posts to the same account a
     * `-fund` charge does, but is kept a distinct intent from
     * `restore-trust-charge-fund` because it records a different real event,
     * not merely a different operator guess at the same one.
     */
    'bank-statement-line.record-trust-charge-vat-redirect': { source: 'bank-statement-line' },
    /** A bank refund or other credit against a Fund-claimable trust bank charge, restoring the LPFF interest payable account. */
    'bank-statement-line.restore-trust-charge-fund': { source: 'bank-statement-line' },
    /** The firm reimbursing the trust for a firm-borne charge, restoring the Recoverable from Business account. */
    'bank-statement-line.restore-trust-charge-firm': { source: 'bank-statement-line' },
    // NOTE: there is deliberately no `bank-transfer.*` intent. The bank-transfer
    // domain never posts a journal itself - it delegates to
    // `reconcileBankStatementLineSplitsOnly` / `...MoneyIn` / `...MoneyOut`, so a
    // transfer leg's journal IS a bank-line allocation and carries
    // `bank-statement-line.allocate`. The transfer remains identifiable through
    // the existing `bankTransferId` open-item tag on the Transfer Clearing leg.
    // An intent no code path can write would be dead weight in a catalogue whose
    // whole point is that declaring an intent is what makes it postable.
    // --- asset ----------------------------------------------------------------
    /** One depreciation period's charge for one asset. */
    'asset.depreciate': { source: 'asset' },
    /** Derecognise one fixed asset on sale or write-off. No cash receipt is created. */
    'asset.dispose': { source: 'asset' },
    // --- ledger (no external source record) ---------------------------------
    /** Business opening balances, posted by bulk import. */
    'ledger.open-business': { source: null },
    /** Trust opening balances. */
    'ledger.open-trust': { source: null },
    /** Manual journal posted by a user through the ledger route. */
    'ledger.post-manual-journal': { source: null },
    /**
     * Backfill sentinel. Stamped ONLY by the migration that adds the column,
     * onto pre-feature rows whose reason could not be recovered from an existing
     * foreign key. No listener keys on it, and the journals UI renders no source
     * link for it. Never posted by application code.
     */
    'ledger.backfill-intent': { source: null },
};
/**
 * The two journals an invoice approval may post under its single approval
 * idempotency key. Sharing the key makes the unique slot the exclusion between
 * ordinary recognition and an opening item.
 */
export const INVOICE_APPROVAL_INTENTS = ['invoice.issue', 'invoice.retag-opening'];
export const LEDGER_BASE_INTENTS = Object.keys(LEDGER_INTENTS);
/**
 * Runtime membership set: every base intent plus its `.reversal` derivative.
 * Backs `isLedgerIntent` and the choke-point validation, which must fail loud on
 * an unknown string arriving from a path that bypasses `tsc` (the seed scripts
 * genuinely do).
 */
const ALL_LEDGER_INTENTS = new Set([
    ...LEDGER_BASE_INTENTS,
    ...LEDGER_BASE_INTENTS.map((base) => `${base}.reversal`),
]);
/**
 * Narrow a free-form string into `LedgerIntent`. Use at trust boundaries - a
 * loaded DB row, an event payload - where a string arrives that should be an
 * intent but is not statically typed as one.
 */
export function isLedgerIntent(value) {
    return ALL_LEDGER_INTENTS.has(value);
}
const REVERSAL_SUFFIX = '.reversal';
/** True for a reversal intent (`invoice.write-off.reversal`). */
export function isReversalIntent(intent) {
    return intent.endsWith(REVERSAL_SUFFIX);
}
/**
 * Strip a trailing `.reversal`, yielding the intent the reversal undoes. A base
 * intent is returned unchanged, so this is safe to call unconditionally before
 * looking anything up in `LEDGER_INTENTS`.
 */
export function baseIntentOf(intent) {
    return (intent.endsWith(REVERSAL_SUFFIX) ? intent.slice(0, -REVERSAL_SUFFIX.length) : intent);
}
/**
 * The domain whose record `sourceEntityId` points at, or `null` when the intent
 * declares no source (manual journals, opening balances, the backfill sentinel).
 *
 * A reversal inherits its original's source domain, so this resolves through the
 * `.reversal` suffix.
 */
export function sourceDomainOf(intent) {
    return LEDGER_INTENTS[baseIntentOf(intent)].source;
}
/**
 * The intent a reversal of `originalIntent` should carry.
 *
 * Takes `string | null` rather than `LedgerIntent` because it is fed straight
 * from a loaded DB row, where the column is nullable for the deploy window and
 * could in principle hold an unrecognised value. Either of those degrades to
 * `ledger.backfill-intent.reversal` - "we reversed something whose reason we do not
 * know" - which is honest, and keeps legacy rows reversible. Reversing legacy,
 * non-conforming sets is a supported path (see the guard's carve-out docblock),
 * so this must never throw.
 */
export function reversalIntentOf(originalIntent) {
    const base = originalIntent !== null && isLedgerIntent(originalIntent)
        ? baseIntentOf(originalIntent)
        : 'ledger.backfill-intent';
    return `${base}${REVERSAL_SUFFIX}`;
}
/**
 * The intents that post opening balances - a direct `Dr Bank / Cr Equity` set
 * with no clearing leg, which is why they are carved out of the
 * bank-movement guard.
 */
const OPENING_BALANCE_BASE_INTENTS = [
    'ledger.open-business',
    'ledger.open-trust',
];
const OPENING_BALANCE_INTENTS = new Set(OPENING_BALANCE_BASE_INTENTS);
/**
 * Every opening-balance intent VALUE, base and `.reversal` alike - the same set
 * `isOpeningBalanceIntent` answers for, as a list a database query can filter
 * on.
 *
 * A brought-forward balance is neither a receipt nor a disbursement, so a
 * report of money received and paid (the cashbook an administrator files under
 * Insolvency Act s 92) has to be able to exclude these sets from its movement
 * list and fold their bank legs into the opening position instead. Derived from
 * the one base list above rather than hand-written, so an intent cannot be a
 * conversion posting for the guard and an ordinary movement for a reader.
 *
 * The reversal of a conversion is in here for the same reason its original is:
 * a restatement of an opening position is still an opening position, and
 * excluding the original while rendering its mirror would show a movement that
 * undoes something the record never showed happening.
 */
export const OPENING_BALANCE_INTENT_VALUES = [
    ...OPENING_BALANCE_BASE_INTENTS,
    ...OPENING_BALANCE_BASE_INTENTS.map((base) => `${base}.reversal`),
];
/**
 * True for an opening-balance posting, INCLUDING the reversal of one
 * (`ledger.open-business.reversal` resolves through `baseIntentOf`).
 *
 * That overlap is deliberate and is why `enforceBankMovementInvariants`
 * must test `isReversalIntent` FIRST: an opening-balance reversal satisfies both
 * predicates, but its idempotency key is `reversal:<id>`, so the opening-balance
 * branch's key assertion would reject it.
 */
export function isOpeningBalanceIntent(intent) {
    return OPENING_BALANCE_INTENTS.has(baseIntentOf(intent));
}
/**
 * The domain whose document this movement acted ON, or `null` when the intent
 * names no counterparty.
 *
 * Distinct from `sourceDomainOf`, and the two must not be confused:
 * `sourceDomainOf` says what `sourceEntityId` points at (for
 * `payment.invoice.settle`, the PAYMENT); this says what the movement was done
 * to (the INVOICE). The counterparty document itself is not on the entry set -
 * it is carried per-leg by the open-item tags - so this classifies, it does not
 * resolve to an id.
 */
export function counterpartyDomainOf(intent) {
    const parts = baseIntentOf(intent).split('.');
    return parts.length === 3 ? parts[1] : null;
}
/** Whether a posting with this intent must carry a `sourceEntityId`. */
export function intentRequiresSource(intent) {
    return sourceDomainOf(intent) !== null;
}
/**
 * Wire schema for the `intent` column, derived from the catalogue rather than
 * hand-listed - a new intent becomes serialisable by being declared above and
 * nowhere else, which is the same property the runtime choke point relies on.
 *
 * A closed enum rather than `z.string()` because this is what the journals UI
 * switches on to decide where its source link points: an unrecognised value
 * must fail at the API boundary, where it is attributable, rather than reach a
 * component that silently renders no link. It is safe to close because the
 * column is NOT NULL and every historical row was stamped
 * `ledger.backfill-intent`, which is itself declared.
 *
 * `as [LedgerIntent, ...LedgerIntent[]]` satisfies `z.enum`'s non-empty-tuple
 * constraint, which `Object.keys` cannot prove; the catalogue is a non-empty
 * object literal, so the assertion holds by construction.
 */
export const LedgerIntentSchema = z.enum([
    ...LEDGER_BASE_INTENTS,
    ...LEDGER_BASE_INTENTS.map((base) => `${base}${REVERSAL_SUFFIX}`),
]);
